/**
 * CalculatorBlock — single component that renders any of the 7 Lone Star tools.
 *
 * Each tool has its own config in the TOOLS map:
 *   pattern: "live" or "wizard"
 *   fields: input definitions
 *   calculate: pure function inputs -> results
 *   renderResults: results -> JSX
 *
 * This keeps 7 tools in a single file (~600 lines) rather than 7 route files.
 */
import React, { useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Download, Check, X, DollarSign, ClipboardCheck, ShieldCheck, Calculator, Palette, Clock, Grid3x3, Flame } from "lucide-react";

const currency = (n) => `$${Math.round(n).toLocaleString()}`;

// ============================================================
// TOOL CONFIGS
// ============================================================
const TOOLS = {
  "cost-calculator": {
    pattern: "live",
    icon: DollarSign,
    fields: [
      { key: "sqft", label: "Total lot square footage", type: "number", default: 100000, min: 1000, max: 1000000, step: 1000 },
      { key: "stalls", label: "Total parking spaces", type: "number", default: 300, min: 10, max: 5000 },
      { key: "ada_stalls", label: "Accessible (ADA) stalls", type: "number", default: 8, min: 0, max: 200 },
      { key: "fire_lane_lf", label: "Fire lane linear feet", type: "number", default: 400, min: 0, max: 10000, step: 25 },
      { key: "scope", label: "Scope", type: "select", default: "restripe", options: [
        { value: "restripe", label: "Full restripe" },
        { value: "new_layout", label: "New layout / redesign" },
        { value: "touch_up", label: "Touch-up only" },
      ]},
      { key: "condition", label: "Pavement condition", type: "select", default: "fair", options: [
        { value: "good", label: "Good (recent sealcoat)" },
        { value: "fair", label: "Fair (typical)" },
        { value: "poor", label: "Poor (heavy contamination/cracks)" },
      ]},
    ],
    calculate: (inp) => {
      const sqft = +inp.sqft || 0;
      const stalls = +inp.stalls || 0;
      const ada = +inp.ada_stalls || 0;
      const fl = +inp.fire_lane_lf || 0;
      const base = { restripe: 0.10, new_layout: 0.15, touch_up: 0.04 }[inp.scope || "restripe"];
      const condMult = { good: 0.85, fair: 1.0, poor: 1.25 }[inp.condition || "fair"];
      const stripe = sqft * base * condMult;
      const adaCost = ada * 575;
      const flCost = fl * 3.75;
      const prep = (inp.condition === "poor" ? 0.12 : 0.06) * (stripe + adaCost);
      const mob = inp.scope === "touch_up" ? 400 : 850;
      const sub = stripe + adaCost + flCost + prep + mob;
      const cont = sub * 0.08;
      return {
        line_items: [
          { label: `Stripe scope (${inp.scope || "restripe"})`, value: stripe },
          { label: `ADA stalls (${ada} × $575)`, value: adaCost },
          { label: `Fire lane (${fl} lf × $3.75)`, value: flCost },
          { label: `Surface prep (${inp.condition || "fair"} condition)`, value: prep },
          { label: "Mobilization", value: mob },
          { label: "Contingency (8%)", value: cont },
        ],
        total: sub + cont,
        duration_nights: Math.max(1, Math.round(sqft / 25000)),
      };
    },
    renderResults: (r, tenant) => (
      <div className="space-y-4">
        <ResultBig label="Estimated total" value={currency(r.total)} tenant={tenant} />
        <ResultLineItems items={r.line_items} total={r.total} tenant={tenant} />
        <ResultBadge label="Estimated duration" value={`${r.duration_nights} nights (phased)`} tenant={tenant} />
      </div>
    ),
  },
  "parking-space-calculator": {
    pattern: "live",
    icon: Grid3x3,
    fields: [
      { key: "length_ft", label: "Lot length (feet)", type: "number", default: 300, min: 50 },
      { key: "width_ft", label: "Lot width (feet)", type: "number", default: 200, min: 50 },
      { key: "layout", label: "Stall angle", type: "select", default: "90", options: [
        { value: "90", label: "90° (perpendicular) — highest density" },
        { value: "60", label: "60° (angled)" },
        { value: "45", label: "45° (angled)" },
      ]},
    ],
    calculate: (inp) => {
      const length = +inp.length_ft || 0;
      const width = +inp.width_ft || 0;
      const aisleWidth = { "90": 24, "60": 18, "45": 15 }[inp.layout || "90"];
      const stallLength = { "90": 18, "60": 20, "45": 21 }[inp.layout || "90"];
      const bayDepth = 2 * stallLength + aisleWidth;
      const bays = Math.max(1, Math.floor(length / bayDepth));
      const perRow = Math.max(0, Math.floor((width - 4) / 9));
      const totalStalls = bays * perRow * 2;
      const adaReq = adaRatio(totalStalls);
      const vanReq = Math.max(1, Math.floor(adaReq / 6));
      return { total_stalls: totalStalls, ada_required: adaReq, van_required: vanReq, bays, stalls_per_row: perRow, layout: inp.layout };
    },
    renderResults: (r, tenant) => (
      <div className="space-y-4">
        <ResultBig label="Total stalls that fit" value={r.total_stalls.toLocaleString()} tenant={tenant} />
        <div className="grid grid-cols-2 gap-3">
          <ResultBadge label="ADA required" value={r.ada_required} tenant={tenant} />
          <ResultBadge label="Van-accessible" value={r.van_required} tenant={tenant} />
          <ResultBadge label="Bays" value={r.bays} tenant={tenant} />
          <ResultBadge label="Stalls per row" value={r.stalls_per_row} tenant={tenant} />
        </div>
      </div>
    ),
  },
  "maintenance-budget": {
    pattern: "live",
    icon: Calculator,
    fields: [
      { key: "sqft", label: "Total lot square footage", type: "number", default: 100000, min: 1000, step: 1000 },
      { key: "stalls", label: "Total parking spaces", type: "number", default: 300, min: 10 },
      { key: "years", label: "Planning horizon (years)", type: "number", default: 5, min: 1, max: 10 },
      { key: "include_sealcoat", label: "Include sealcoating cycles", type: "checkbox", default: true },
      { key: "include_ada_reserve", label: "Include ADA remediation reserve", type: "checkbox", default: true },
    ],
    calculate: (inp) => {
      const sqft = +inp.sqft || 0;
      const stalls = +inp.stalls || 0;
      const years = +inp.years || 5;
      const restripeCost = sqft * 0.10;
      const touchUp = stalls * 0.10 * 12;
      const sealcoat = inp.include_sealcoat !== false ? sqft * 0.20 : 0;
      const signage = stalls * 0.5;
      const curb = 800;
      const ada = inp.include_ada_reserve !== false ? stalls * 15 : 0;
      const emergency = 0.10 * (touchUp + signage + curb + ada);
      const per_year = [];
      let total = 0;
      for (let y = 1; y <= years; y++) {
        const restripe = y % 3 === 0 ? restripeCost : 0;
        const sc = y % 4 === 0 ? sealcoat : 0;
        const yt = restripe + sc + touchUp + signage + curb + ada + emergency;
        total += yt;
        per_year.push({ year: y, restripe, sealcoat: sc, touch_up: touchUp, signage, curb, ada_reserve: ada, emergency, total: yt });
      }
      return { years, per_year, grand_total: total, annual_avg: total / years };
    },
    renderResults: (r, tenant) => (
      <div className="space-y-4">
        <ResultBig label={`${r.years}-year total`} value={currency(r.grand_total)} tenant={tenant} />
        <ResultBadge label="Annual average" value={currency(r.annual_avg)} tenant={tenant} />
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono-plex">
            <thead><tr>
              {["Yr", "Restripe", "Sealcoat", "Touch-up", "Signage", "Curb", "ADA", "Emerg", "Total"].map((h) => (
                <th key={h} className="p-2 text-left border" style={{ borderColor: "var(--l-border)", color: "var(--l-text-sec)" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {r.per_year.map((py) => (
                <tr key={py.year}>
                  <td className="p-2 border font-bold" style={{ borderColor: "var(--l-border)" }}>{py.year}</td>
                  <td className="p-2 border" style={{ borderColor: "var(--l-border)" }}>{currency(py.restripe)}</td>
                  <td className="p-2 border" style={{ borderColor: "var(--l-border)" }}>{currency(py.sealcoat)}</td>
                  <td className="p-2 border" style={{ borderColor: "var(--l-border)" }}>{currency(py.touch_up)}</td>
                  <td className="p-2 border" style={{ borderColor: "var(--l-border)" }}>{currency(py.signage)}</td>
                  <td className="p-2 border" style={{ borderColor: "var(--l-border)" }}>{currency(py.curb)}</td>
                  <td className="p-2 border" style={{ borderColor: "var(--l-border)" }}>{currency(py.ada_reserve)}</td>
                  <td className="p-2 border" style={{ borderColor: "var(--l-border)" }}>{currency(py.emergency)}</td>
                  <td className="p-2 border font-bold" style={{ borderColor: "var(--l-border)", color: tenant.primary_color }}>{currency(py.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ),
  },
  "paint-estimator": {
    pattern: "live",
    icon: Palette,
    fields: [
      { key: "linear_ft", label: "Total line linear feet", type: "number", default: 5000 },
      { key: "stall_count", label: "Total stalls to stripe", type: "number", default: 300 },
      { key: "ada_count", label: "Accessible stalls (blue field)", type: "number", default: 8 },
      { key: "arrow_count", label: "Directional arrows / symbols", type: "number", default: 20 },
      { key: "line_width", label: "Line width (inches)", type: "number", default: 4, step: 0.5 },
      { key: "mils", label: "Application thickness (mils wet)", type: "number", default: 18, step: 1 },
    ],
    calculate: (inp) => {
      const lf = +inp.linear_ft || 0;
      const stalls = +inp.stall_count || 0;
      const ada = +inp.ada_count || 0;
      const arrows = +inp.arrow_count || 0;
      const lw = +inp.line_width || 4;
      const mils = +inp.mils || 18;
      const coverage = 130 * (15 / mils) * (4 / lw);
      const lineSqft = (lf * lw / 12) + (stalls * 24) + (ada * 40) + (arrows * 6);
      return {
        line_sqft: lineSqft,
        gallons_white: (lineSqft / coverage) + 1,
        gallons_yellow: ((arrows * 6 + ada * 40) / coverage) + 1,
        gallons_blue: (ada * 40 / coverage) + 0.5,
      };
    },
    renderResults: (r, tenant) => (
      <div className="space-y-4">
        <ResultBig label="Paint required" value={`${r.gallons_white.toFixed(1)} gal white`} tenant={tenant} />
        <div className="grid grid-cols-2 gap-3">
          <ResultBadge label="Yellow" value={`${r.gallons_yellow.toFixed(1)} gal`} tenant={tenant} />
          <ResultBadge label="Blue" value={`${r.gallons_blue.toFixed(1)} gal`} tenant={tenant} />
          <ResultBadge label="Total line area" value={`${r.line_sqft.toFixed(0)} sqft`} tenant={tenant} />
        </div>
      </div>
    ),
  },
  "timeline-estimator": {
    pattern: "live",
    icon: Clock,
    fields: [
      { key: "sqft", label: "Total lot square footage", type: "number", default: 100000, step: 1000 },
      { key: "scope", label: "Scope", type: "select", default: "restripe", options: [
        { value: "touch_up", label: "Touch-up only" },
        { value: "restripe", label: "Full restripe" },
        { value: "new_layout", label: "New layout / redesign" },
      ]},
      { key: "occupied", label: "Property occupied during work?", type: "checkbox", default: true },
      { key: "weather_risk", label: "Book during a rainy season?", type: "checkbox", default: false },
    ],
    calculate: (inp) => {
      const productivity = { touch_up: 60000, restripe: 40000, new_layout: 25000 }[inp.scope || "restripe"];
      let shifts = Math.max(1, Math.round((+inp.sqft || 0) / productivity));
      if (inp.occupied !== false) shifts += 1;
      const cal = shifts + (inp.weather_risk ? 2 : 0);
      return { shifts, calendar_days: cal };
    },
    renderResults: (r, tenant) => (
      <div className="space-y-4">
        <ResultBig label="Estimated shifts" value={`${r.shifts} nights`} tenant={tenant} />
        <ResultBadge label="Calendar days" value={`${r.calendar_days} days`} tenant={tenant} />
      </div>
    ),
  },
  "ada-checklist": {
    pattern: "wizard",
    icon: ShieldCheck,
    questions: [
      { key: "count_ok", label: "Does the number of accessible stalls meet the minimum for total space count?" },
      { key: "dim_width", label: "Are all accessible stalls at least 96 inches wide?" },
      { key: "dim_van", label: "Do van-accessible stalls meet the 132\"+60\" or 96\"+96\" configuration?" },
      { key: "aisle_width", label: "Are all access aisles at least 60 inches wide (or 96\" for shared van)?" },
      { key: "slope", label: "Are slopes ≤ 2% in every direction across the stall and aisle?" },
      { key: "sign_present", label: "Is vertical (post-mounted) signage present at every accessible stall?" },
      { key: "sign_height", label: "Is the bottom of every sign at least 60\" above finished grade?" },
      { key: "sign_tas_fine", label: "Do signs include the Texas TAS-required 'FINE $500-$750' notice?" },
      { key: "symbol_visible", label: "Is the ISA pavement symbol ≥ 36 inches tall and centered?" },
      { key: "route_width", label: "Is the accessible route at least 36 inches wide?" },
      { key: "route_slope", label: "Is the running slope ≤ 5% and cross slope ≤ 2%?" },
      { key: "route_unobstructed", label: "Is the route unobstructed by landscaping, carts, or storage?" },
      { key: "truncated_domes", label: "Are detectable warnings (truncated domes) present at all crossings?" },
    ],
    calculate: (inp) => {
      const ans = inp.answers || {};
      const total = 13;
      const passed = Object.values(ans).filter((v) => v === true).length;
      const failed = Object.values(ans).filter((v) => v === false).length;
      const score = Math.round((passed / total) * 100);
      const tier = score >= 90 ? "Compliant" : score >= 70 ? "At Risk" : "Non-Compliant";
      return { score, tier, total_checks: total, passed, failed };
    },
    renderResults: (r, tenant) => (
      <div className="space-y-4">
        <ResultBig label="ADA compliance score" value={`${r.score}/100`} tenant={tenant} />
        <div className={`text-center py-3 rounded-lg font-cab font-bold uppercase tracking-wider`} style={{ background: r.tier === "Compliant" ? "#16a34a22" : r.tier === "At Risk" ? `${tenant.primary_color}22` : "#dc262622", color: r.tier === "Compliant" ? "#16a34a" : r.tier === "At Risk" ? tenant.primary_color : "#dc2626" }}>
          {r.tier}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <ResultBadge label="Passed" value={r.passed} tenant={tenant} />
          <ResultBadge label="Failed" value={r.failed} tenant={tenant} />
          <ResultBadge label="Total checks" value={r.total_checks} tenant={tenant} />
        </div>
      </div>
    ),
  },
  "inspection-checklist": {
    pattern: "wizard",
    icon: ClipboardCheck,
    questions: [
      { key: "pavement", label: "Rate the pavement condition (1 = severe cracks/heaving, 5 = pristine)" },
      { key: "line_visibility", label: "Rate the line paint visibility (1 = ghosts, 5 = fresh)" },
      { key: "ada", label: "Rate ADA stalls compliance (1 = major violations, 5 = fully compliant)" },
      { key: "fire_lane", label: "Rate fire lane markings and signage (1 = illegible/missing, 5 = fresh + fully signed)" },
      { key: "arrows", label: "Rate directional arrows and stop bars (1 = missing/faded, 5 = fresh + complete)" },
      { key: "curbs", label: "Rate curb painting condition and color coding (1 = faded/wrong colors, 5 = fresh + correctly coded)" },
      { key: "crosswalks", label: "Rate pedestrian crosswalks (1 = missing/faded, 5 = fresh continental)" },
      { key: "signage", label: "Rate signage condition (1 = damaged/missing, 5 = fresh + fully compliant)" },
      { key: "bollards", label: "Rate bollards and wheel stops (1 = missing/damaged, 5 = all present + freshly painted)" },
      { key: "speed_bumps", label: "Rate speed bumps and traffic calming (1 = worn/unmarked, 5 = fresh chevron markings)" },
      { key: "drainage", label: "Rate drainage and standing water (1 = severe pooling, 5 = no standing water)" },
      { key: "landscaping", label: "Rate landscaping encroachment on drive aisles (1 = major obstruction, 5 = no encroachment)" },
    ],
    calculate: (inp) => {
      const ans = inp.answers || {};
      const scores = Object.values(ans).map(v => +v || 0);
      const total = scores.reduce((a, b) => a + b, 0);
      const max = scores.length * 5;
      const pct = max ? Math.round((total / max) * 100) : 0;
      const tier = pct >= 85 ? "Excellent" : pct >= 70 ? "Good" : pct >= 50 ? "Deferred maintenance" : "Immediate attention";
      return { score: pct, tier, total, max_total: max };
    },
    renderResults: (r, tenant) => (
      <div className="space-y-4">
        <ResultBig label="Property score" value={`${r.score}/100`} tenant={tenant} />
        <div className="text-center py-3 rounded-lg font-cab font-bold uppercase tracking-wider" style={{ background: r.score >= 70 ? "#16a34a22" : `${tenant.primary_color}22`, color: r.score >= 70 ? "#16a34a" : tenant.primary_color }}>
          {r.tier}
        </div>
        <ResultBadge label="Raw score" value={`${r.total} / ${r.max_total}`} tenant={tenant} />
      </div>
    ),
  },
  "fire-lane-calculator": {
    pattern: "live",
    icon: Flame,
    fields: [
      { key: "building_height_ft", label: "Tallest building floor height above lowest fire-department access level (ft)", type: "number", default: 24, min: 0, max: 500, step: 1 },
      { key: "num_stories", label: "Number of stories in the tallest building on the property", type: "number", default: 2, min: 1, max: 100 },
      { key: "occupancy_type", label: "Primary occupancy type (IBC)", type: "select", default: "business", options: [
        { value: "business", label: "Business (B) — office, medical clinic" },
        { value: "mercantile", label: "Mercantile (M) — retail" },
        { value: "residential", label: "Residential (R) — apartments, hotels" },
        { value: "assembly", label: "Assembly (A) — church, restaurant, event venue" },
        { value: "storage", label: "Storage / Warehouse (S)" },
        { value: "factory", label: "Factory / Industrial (F)" },
        { value: "high_hazard", label: "High-Hazard (H-1, H-2) — flammable, toxic, explosive" },
      ]},
      { key: "has_sprinklers", label: "Building fully sprinklered (NFPA 13 or 13R)?", type: "select", default: "no", options: [
        { value: "no", label: "No" },
        { value: "yes", label: "Yes" },
      ]},
      { key: "lot_perimeter_lf", label: "Approximate lot perimeter (linear feet) — optional, for curb-marking estimate", type: "number", default: 0, min: 0, max: 20000, step: 25 },
    ],
    calculate: (inp) => {
      const height = +inp.building_height_ft || 0;
      const stories = +inp.num_stories || 1;
      const occupancy = inp.occupancy_type || "business";
      const sprinklered = (inp.has_sprinklers || "no") === "yes";
      const perimeter = +inp.lot_perimeter_lf || 0;
      const aerial = height >= 30 || stories >= 3;
      const highHazard = occupancy === "high_hazard";
      const width = (aerial || highHazard) ? 26 : 20;
      const widthReason = (aerial || highHazard)
        ? "IFC Appendix D105 aerial apparatus access — building qualifies (30 ft+ or 3+ stories or high-hazard occupancy)."
        : "IFC 503.2.1 minimum — standard fire apparatus access.";
      const notes = [
        `Minimum width: ${width} ft measured curb face to curb face — unobstructed by parked vehicles, dumpsters, or landscape.`,
        "Minimum vertical clearance: 13 ft 6 in (per IFC 503.2.1).",
        "Turning radius: 25 ft inside / 45 ft outside (IFC 503.2.4 — local Fire Marshal may require larger).",
        "Grade: no more than 10 % (IFC 503.2.7).",
      ];
      if (sprinklered && width === 26) {
        notes.push("Sprinklered building: Fire code official may authorize reduced width — request a written interpretation from your AHJ (e.g., Dallas Fire-Rescue) before finalizing site plan.");
      }
      notes.push("Red curb marking with 4-inch white “FIRE LANE · NO PARKING” lettering every 25 ft (Dallas Fire-Rescue policy).");
      return {
        required_width_ft: width,
        width_reason: widthReason,
        vertical_clearance: "13'6\"",
        inside_turning_radius_ft: 25,
        outside_turning_radius_ft: 45,
        max_grade_pct: 10,
        aerial_required: aerial,
        high_hazard: highHazard,
        estimated_red_curb_lf: perimeter ? Math.round(perimeter * 0.60) : null,
        notes,
        code_reference: "2021 IFC Ch. 5 §503 · Dallas Fire-Rescue enforcement",
      };
    },
    renderResults: (r, tenant) => (
      <div className="space-y-4">
        <ResultBig label="Minimum fire lane width" value={`${r.required_width_ft} ft`} tenant={tenant} />
        <div
          className="text-xs font-inter leading-relaxed p-3 rounded-md"
          style={{ background: `${tenant.primary_color}12`, color: "var(--l-text-sec)", border: `1px solid ${tenant.primary_color}33` }}
        >
          {r.width_reason}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <ResultBadge label="Vertical clearance" value={r.vertical_clearance} tenant={tenant} />
          <ResultBadge label="Max grade" value={`${r.max_grade_pct}%`} tenant={tenant} />
          <ResultBadge label="Turning radius (inside)" value={`${r.inside_turning_radius_ft} ft`} tenant={tenant} />
          <ResultBadge label="Turning radius (outside)" value={`${r.outside_turning_radius_ft} ft`} tenant={tenant} />
        </div>
        {r.estimated_red_curb_lf ? (
          <ResultBadge label="Estimated red-curb marking (60% of perimeter)" value={`${r.estimated_red_curb_lf.toLocaleString()} lf`} tenant={tenant} />
        ) : null}
        <div>
          <div className="font-cab font-bold uppercase text-xs tracking-wider mb-2" style={{ color: tenant.primary_color }}>Compliance notes</div>
          <ul className="space-y-2 font-inter text-sm leading-relaxed" style={{ color: "var(--l-text-sec)" }}>
            {r.notes.map((n, i) => (
              <li key={i} className="flex gap-2">
                <span aria-hidden style={{ color: tenant.primary_color }}>•</span>
                <span>{n}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="text-[11px] font-inter uppercase tracking-[0.18em] pt-2" style={{ color: "var(--l-text-sec)" }}>
          Reference · {r.code_reference}
        </div>
      </div>
    ),
  },
};

function adaRatio(total) {
  const t = [[25, 1], [50, 2], [75, 3], [100, 4], [150, 5], [200, 6],
             [300, 7], [400, 8], [500, 9], [1000, 20]];
  for (const [th, req] of t) if (total <= th) return req;
  return 20 + Math.floor((total - 1000) / 100) + 1;
}

// ============================================================
// SHARED PRIMITIVES
// ============================================================
function ResultBig({ label, value, tenant }) {
  return (
    <div>
      <div className="font-mono-plex text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--l-text-muted, #7C7C82)" }}>{label}</div>
      <div className="font-cab font-black text-4xl leading-none tracking-tight" style={{ color: "var(--l-text)" }}>{value}</div>
    </div>
  );
}
function ResultBadge({ label, value, tenant }) {
  return (
    <div className="border rounded-md p-3" style={{ borderColor: "var(--l-border)", background: "var(--l-bg)" }}>
      <div className="font-mono-plex text-[9px] uppercase tracking-widest" style={{ color: "var(--l-text-muted, #7C7C82)" }}>{label}</div>
      <div className="font-cab font-bold text-lg tracking-tight" style={{ color: "var(--l-text)" }}>{value}</div>
    </div>
  );
}
function ResultLineItems({ items, total, tenant }) {
  return (
    <div className="border rounded-lg overflow-hidden" style={{ borderColor: "var(--l-border)" }}>
      {items.map((it, i) => (
        <div key={i} className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--l-border)" }}>
          <div className="font-inter text-sm" style={{ color: "var(--l-text-sec)" }}>{it.label}</div>
          <div className="font-mono-plex font-bold text-sm" style={{ color: "var(--l-text)" }}>{currency(it.value)}</div>
        </div>
      ))}
      <div className="flex items-center justify-between px-4 py-3" style={{ background: "var(--l-surface)" }}>
        <div className="font-cab font-bold uppercase text-sm tracking-widest" style={{ color: "var(--l-text)" }}>Total</div>
        <div className="font-cab font-black text-lg" style={{ color: tenant.primary_color }}>{currency(total)}</div>
      </div>
    </div>
  );
}


// ============================================================
// MAIN COMPONENT
// ============================================================
export function CalculatorBlock({ props, tenant }) {
  const { tool_id, headline, subheadline, eyebrow } = props;
  const tool = TOOLS[tool_id];
  if (!tool) return null;
  return tool.pattern === "wizard" ? <WizardTool tool_id={tool_id} tool={tool} tenant={tenant} eyebrow={eyebrow} headline={headline} subheadline={subheadline} />
                                    : <LiveTool tool_id={tool_id} tool={tool} tenant={tenant} eyebrow={eyebrow} headline={headline} subheadline={subheadline} />;
}


// -------- LIVE (single-page form + results) ---------
function LiveTool({ tool_id, tool, tenant, eyebrow, headline, subheadline }) {
  const initial = useMemo(() => Object.fromEntries(tool.fields.map((f) => [f.key, f.default])), [tool_id]);
  const [values, setValues] = useState(initial);
  const results = useMemo(() => tool.calculate(values), [values]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const setField = (k, v) => setValues({ ...values, [k]: v });

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const r = await axios.post(`${API}/tools/estimate-pdf`, {
        tool_id, inputs: values,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${tool_id}-estimate.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } finally { setPdfLoading(false); }
  };

  return (
    <section className="max-w-7xl mx-auto px-6 py-12" data-block="calculator" data-tool-id={tool_id}>
      <div className="mb-10 max-w-3xl">
        {eyebrow && <div className="font-mono-plex text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
        {headline && <h1 className="font-cab font-black text-4xl md:text-5xl leading-tight tracking-tight" style={{ color: "var(--l-text)" }}>{headline}</h1>}
        {subheadline && <p className="font-inter mt-5 text-lg leading-relaxed" style={{ color: "var(--l-text-sec)" }}>{subheadline}</p>}
      </div>
      <div className="grid lg:grid-cols-2 gap-8">
        {/* FORM */}
        <div className="border rounded-lg p-6 space-y-4" style={{ borderColor: "var(--l-border)", background: "var(--l-surface)" }}>
          <div className="font-mono-plex text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: tenant.primary_color }}>Inputs</div>
          {tool.fields.map((f) => (
            <FieldRow key={f.key} field={f} value={values[f.key]} onChange={(v) => setField(f.key, v)} />
          ))}
        </div>
        {/* RESULTS */}
        <div className="border rounded-lg p-6 space-y-6" style={{ borderColor: "var(--l-border)", background: "var(--l-surface)" }}>
          <div className="font-mono-plex text-[10px] uppercase tracking-widest font-bold" style={{ color: tenant.primary_color }}>Live results</div>
          {tool.renderResults(results, tenant)}
          <div className="pt-4 border-t space-y-3" style={{ borderColor: "var(--l-border)" }}>
            <button onClick={downloadPdf} disabled={pdfLoading} className="w-full flex items-center justify-center gap-2 py-3 rounded-md font-cab font-bold text-sm uppercase tracking-wider transition-transform hover:scale-[1.02]" style={{ background: tenant.primary_color, color: "#0D0D0D" }} data-testid="download-estimate-pdf">
              <Download size={16} strokeWidth={2.5} />
              {pdfLoading ? "Generating…" : "Download preliminary estimate PDF"}
            </button>
            <a href="/contact" className="w-full flex items-center justify-center gap-2 py-3 rounded-md border font-cab font-bold text-sm uppercase tracking-wider transition-colors" style={{ borderColor: tenant.primary_color, color: tenant.primary_color }} data-testid="request-real-quote">
              Get a real fixed-price quote →
            </a>
          </div>
          <p className="font-inter text-xs" style={{ color: "var(--l-text-muted, #7C7C82)" }}>
            This is a preliminary estimate based on Texas commercial benchmark pricing. Final quotes require a property walk-through and are typically delivered within 48 hours of a request.
          </p>
        </div>
      </div>
    </section>
  );
}

function FieldRow({ field, value, onChange }) {
  if (field.type === "select") {
    return (
      <label className="block">
        <div className="font-mono-plex text-[10px] uppercase tracking-widest mb-1.5" style={{ color: "var(--l-text-muted, #7C7C82)" }}>{field.label}</div>
        <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border rounded-md font-inter text-sm" style={{ borderColor: "var(--l-border)", background: "var(--l-bg)", color: "var(--l-text)" }} data-testid={`field-${field.key}`}>
          {field.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
    );
  }
  if (field.type === "checkbox") {
    return (
      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4" data-testid={`field-${field.key}`} />
        <span className="font-inter text-sm" style={{ color: "var(--l-text)" }}>{field.label}</span>
      </label>
    );
  }
  return (
    <label className="block">
      <div className="font-mono-plex text-[10px] uppercase tracking-widest mb-1.5" style={{ color: "var(--l-text-muted, #7C7C82)" }}>{field.label}</div>
      <input type="number" value={value} min={field.min} max={field.max} step={field.step || 1} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border rounded-md font-inter text-sm" style={{ borderColor: "var(--l-border)", background: "var(--l-bg)", color: "var(--l-text)" }} data-testid={`field-${field.key}`} />
    </label>
  );
}


// -------- WIZARD (Q1 -> Q2 -> ... -> Results) ---------
function WizardTool({ tool_id, tool, tenant, eyebrow, headline, subheadline }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [pdfLoading, setPdfLoading] = useState(false);
  const isChecklist = tool.questions[0] && !tool.questions[0].key.startsWith("rate"); // yes/no vs rating
  const isRating = tool_id === "inspection-checklist";
  const done = step >= tool.questions.length;
  const answer = (val) => {
    const q = tool.questions[step];
    setAnswers({ ...answers, [q.key]: val });
    setStep(step + 1);
  };
  const back = () => setStep(Math.max(0, step - 1));
  const restart = () => { setStep(0); setAnswers({}); };
  const results = useMemo(() => tool.calculate({ answers }), [answers, tool_id]);

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const r = await axios.post(`${API}/tools/estimate-pdf`, {
        tool_id, inputs: { answers },
      }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${tool_id}-report.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } finally { setPdfLoading(false); }
  };

  return (
    <section className="max-w-4xl mx-auto px-6 py-12" data-block="calculator" data-tool-id={tool_id}>
      <div className="mb-8 max-w-3xl">
        {eyebrow && <div className="font-mono-plex text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
        {headline && <h1 className="font-cab font-black text-4xl md:text-5xl leading-tight tracking-tight" style={{ color: "var(--l-text)" }}>{headline}</h1>}
        {subheadline && <p className="font-inter mt-5 text-lg leading-relaxed" style={{ color: "var(--l-text-sec)" }}>{subheadline}</p>}
      </div>
      {!done ? (
        <div className="border rounded-lg p-8" style={{ borderColor: "var(--l-border)", background: "var(--l-surface)" }}>
          <div className="flex items-center justify-between mb-6">
            <div className="font-mono-plex text-[11px] uppercase tracking-widest font-bold" style={{ color: tenant.primary_color }}>Question {step + 1} of {tool.questions.length}</div>
            <div className="flex-1 mx-4 h-1 rounded-full overflow-hidden" style={{ background: "var(--l-bg)" }}>
              <div className="h-full transition-all" style={{ width: `${((step) / tool.questions.length) * 100}%`, background: tenant.primary_color }} />
            </div>
          </div>
          <div className="font-cab font-black text-2xl leading-tight tracking-tight uppercase mb-8" style={{ color: "var(--l-text)" }}>
            {tool.questions[step].label}
          </div>
          {isRating ? (
            <div className="grid grid-cols-5 gap-2 mb-4">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => answer(n)} className="py-4 rounded-md border font-cab font-black text-xl transition-all hover:-translate-y-0.5" style={{ borderColor: "var(--l-border)", background: "var(--l-bg)", color: "var(--l-text)" }} data-testid={`wizard-answer-${n}`}>{n}</button>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button onClick={() => answer(true)} className="py-4 rounded-md border font-cab font-bold uppercase tracking-wider transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2" style={{ borderColor: "var(--l-border)", background: "var(--l-bg)", color: "var(--l-text)" }} data-testid="wizard-answer-yes">
                <Check size={18} strokeWidth={2.5} style={{ color: "#16a34a" }} /> Yes
              </button>
              <button onClick={() => answer(false)} className="py-4 rounded-md border font-cab font-bold uppercase tracking-wider transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2" style={{ borderColor: "var(--l-border)", background: "var(--l-bg)", color: "var(--l-text)" }} data-testid="wizard-answer-no">
                <X size={18} strokeWidth={2.5} style={{ color: "#dc2626" }} /> No
              </button>
            </div>
          )}
          <div className="flex items-center justify-between pt-4">
            <button onClick={back} disabled={step === 0} className="font-mono-plex text-xs uppercase tracking-widest disabled:opacity-30" style={{ color: "var(--l-text-muted, #7C7C82)" }}>← Back</button>
            <button onClick={restart} className="font-mono-plex text-xs uppercase tracking-widest" style={{ color: "var(--l-text-muted, #7C7C82)" }}>Restart</button>
          </div>
        </div>
      ) : (
        <div className="border rounded-lg p-8" style={{ borderColor: "var(--l-border)", background: "var(--l-surface)" }}>
          <div className="font-mono-plex text-[10px] uppercase tracking-widest font-bold mb-6" style={{ color: tenant.primary_color }}>Results</div>
          {tool.renderResults(results, tenant)}
          <div className="pt-6 mt-6 border-t space-y-3" style={{ borderColor: "var(--l-border)" }}>
            <button onClick={downloadPdf} disabled={pdfLoading} className="w-full flex items-center justify-center gap-2 py-3 rounded-md font-cab font-bold text-sm uppercase tracking-wider transition-transform hover:scale-[1.02]" style={{ background: tenant.primary_color, color: "#0D0D0D" }} data-testid="download-report-pdf">
              <Download size={16} strokeWidth={2.5} />
              {pdfLoading ? "Generating…" : "Download PDF report"}
            </button>
            <a href="/contact" className="w-full flex items-center justify-center gap-2 py-3 rounded-md border font-cab font-bold text-sm uppercase tracking-wider transition-colors" style={{ borderColor: tenant.primary_color, color: tenant.primary_color }} data-testid="request-real-quote">
              Get a fixed-price commercial quote →
            </a>
            <button onClick={restart} className="w-full py-3 rounded-md font-mono-plex text-xs uppercase tracking-widest" style={{ color: "var(--l-text-muted, #7C7C82)" }}>Restart walkthrough</button>
          </div>
        </div>
      )}
    </section>
  );
}
